import { Dialog } from "components/Dialog";
import { useFormik } from "formik";
import { IUserRow, PatchedUser, UserType } from "interfaces/user";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import * as yup from "yup";

import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { UserProfileContext } from "contexts/UserProfileContext";
import { useUserProfileQuery } from "hooks/queries/useUserProfileQuery";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  API_URL_GET_CAN_EDIT_INTERNAL_USERS,
  API_URL_USER_PROFILE
} from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { GenericResponse } from "interfaces/utils";

const StyledDivDialogContent = styled("div")<{ widthPx: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: ${props => props.widthPx}px;
`;

const validationSchema = yup.object({
  firstName: yup
    .string()
    .nullable()
    .max(150, "First Name must be less than 150 characters")
    .required("First Name is required"),
  lastName: yup
    .string()
    .nullable()
    .max(150, "Last Name must be less than 150 characters")
    .required("Last Name is required"),
  email: yup
    .string()
    .email("Enter a valid email address")
    .max(254, "Email must be less than 254 characters")
    .required("Email is required"),
  userType: yup
    .string()
    .nullable()
    .required("User Type is required")
});

const SIMPLE_ROLES = [
  {
    id: "customer-admin",
    name: "Admin"
  },
  {
    id: "customer-user",
    name: "User"
  }
];

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  userType: UserType;
  roles: string;
}

export interface ICreateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (user: PatchedUser) => void;
  canSelectRoles?: boolean;
  loading?: boolean;
  dialogWidthPx?: number;
  user: IUserRow;
}

export function EditUserDialog({
  isOpen,
  onClose,
  onConfirm,
  loading,
  dialogWidthPx = 500,
  user
}: ICreateUserDialogProps): JSX.Element {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { axios } = useContext(AxiosContext)!;
  const { currentUserId } = useContext(UserProfileContext);
  const {
    data: userProfileData,
    isLoading: userProfileLoading,
    isError: userProfileError
  } = useUserProfileQuery(user?.id);

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      userType: user?.userType,
      roles: user?.role
    },
    validationSchema,
    onSubmit: values => {
      handleSubmit(formik.isValid && !formHasErrors, values);
    },
    enableReinitialize: true
  });

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.resetForm();
    }
    // adding Formik to deps causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: canEditInternalUsers } = useQuery<boolean>({
    queryKey: ["can-edit-internal-users"],
    queryFn: () =>
      axios
        .get<boolean>(API_URL_GET_CAN_EDIT_INTERNAL_USERS)
        .then(r => r?.data || false),
    refetchOnWindowFocus: false
  });

  const reinviteUser = useMutation(["patch-user"], {
    mutationFn: (userId: number) => {
      return axios
        .post<GenericResponse>(`${API_URL_USER_PROFILE}${userId}/resend_invite`)
        .then(r => r.data);
    },
    onSuccess: () =>
      addSnackbarToQueue({
        severity: "success",
        message: "Successfully reinvited user."
      }),
    onError: () =>
      addSnackbarToQueue({
        severity: "error",
        message: "Error re-inviting user."
      })
  });

  const handleReinvite = (): void => {
    reinviteUser.mutateAsync(user?.id);
  };

  const canEditName = (user: IUserRow): boolean => user?.isEditable;
  const canEditEmail = (): boolean => false;
  const canEditType = (): boolean =>
    user?.id !== currentUserId &&
    !user?.isOnyxUser &&
    (canEditInternalUsers ?? false);
  const canEditRoles = (user: IUserRow): boolean =>
    user?.isEditable &&
    !user?.isOnyxUser &&
    user?.id !== currentUserId &&
    user?.userType !== UserType.EXTERNAL;

  const formHasErrors = useMemo(() => !formik.isValid, [formik.isValid]);

  const handleSubmit = useCallback(
    (isValid: boolean, values: FormValues): void => {
      if (!isValid) return;
      onConfirm({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        userType: values.userType as UserType,
        roles: values.roles
      } as PatchedUser);
    },
    [onConfirm]
  );

  const userTypeOptions = [UserType.INTERNAL, UserType.EXTERNAL];

  const isLoading = loading;

  return (
    <Dialog
      dialogId="dialog-edit-user"
      open={isOpen}
      title="Edit User"
      confirmText="Save"
      confirmDisabled={formHasErrors}
      size="large"
      actionsDisabled={isLoading}
      errorText={
        formik.submitCount > 0 && formHasErrors ? "Validation error" : undefined
      }
      onSubmit={formik.handleSubmit}
      onClose={() => {
        onClose();
        formik.resetForm();
        formik.setErrors({});
      }}
      aria-label="Edit user dialog"
      data-qa="UM-Dialog-Edit-User"
      allowClickaway={false}
      fullWidth={false}
    >
      <StyledDivDialogContent widthPx={dialogWidthPx}>
        <TextField
          variant="outlined"
          fullWidth
          name="firstName"
          label="First Name"
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
          value={formik.values.firstName}
          disabled={!canEditName({ ...user!, ...formik.values })}
        />
        <TextField
          variant="outlined"
          fullWidth
          name="lastName"
          label="Last Name"
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
          value={formik.values.lastName}
          disabled={!canEditName({ ...user!, ...formik.values })}
        />
        <TextField
          variant="outlined"
          fullWidth
          name="email"
          label="Email"
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          value={formik.values.email}
          disabled={!canEditEmail()}
        />
        <FormControl variant="outlined">
          <InputLabel id="user-type-select-label">Type</InputLabel>
          <Select
            id="userType"
            name="userType"
            label="Type"
            labelId="user-type-select-label"
            variant="outlined"
            data-testid="create-user-select-userType"
            displayEmpty
            onChange={event => {
              formik.handleChange(event);
              if (event.target.value === UserType.EXTERNAL) {
                formik.setFieldValue("roles", user.role);
              }
            }}
            onBlur={formik.handleBlur}
            value={formik.values.userType}
            error={formik.touched.userType && Boolean(formik.errors.userType)}
            disabled={!canEditType()}
          >
            {userTypeOptions.map(userType => (
              <MenuItem key={userType} value={userType}>
                {userType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {formik.values.userType === UserType.INTERNAL && (
          <FormControl variant="outlined">
            <InputLabel id="user-role-select-label">Role</InputLabel>
            <Select
              name="roles"
              label="Role"
              labelId="user-role-select-label"
              variant="outlined"
              value={formik.values.roles}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              data-testid="create-user-select-role"
              error={formik.touched.roles && Boolean(formik.errors.roles)}
              disabled={
                loading || !canEditRoles({ ...user!, ...formik.values })
              }
            >
              {SIMPLE_ROLES.map(role => (
                <MenuItem key={role.id} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!userProfileLoading &&
          !userProfileError &&
          !userProfileData.last_login && (
            <>
              <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.24)" }} />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography>Last Login: Never</Typography>

                <Button
                  type="button"
                  onClick={handleReinvite}
                  variant="outlined"
                >
                  Resend Invite
                </Button>
              </Stack>
            </>
          )}
      </StyledDivDialogContent>
    </Dialog>
  );
}
